import { Link, useMatches, useRouter } from '@tanstack/react-router';
import type React from 'react';
import { Suspense, use, useRef } from 'react';

import { MenuLink } from './MenuLink';
import classes from './Sidebar.module.css';
import { UserMenu } from './UserMenu';
import { ProjectContext } from '#contexts/ProjectContext';

export function Sidebar() {
  const { config } = use(ProjectContext);
  const { routesByPath } = useRouter();
  const currentMatch = useMatches().at(-1);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const LogoComponent = config?.logo;
  const sidebarEntries = Object.entries(routesByPath)
    .map(([path, route]) => [path, route.options.staticData?.route])
    .filter(([, route]) => route?.sidebarPosition !== undefined)
    .toSorted(([, a], [, b]) => a.sidebarPosition - b.sidebarPosition)
    .map(([path, { icon, title }]) => (
      <MenuLink
        key={path}
        icon={icon}
        from={currentMatch?.fullPath}
        to={path}
        title={title}
        params={currentMatch?.params}
      />
    ));

  return (
    <>
      <nav className={classes.sidebar}>
        <Link className={classes.logo} to="/">
          <LogoComponent />
        </Link>

        {sidebarEntries}

        <div className={classes.additionalEntries}>
          {customSidebarEntries.map(([name, Component]) => (
            <Suspense key={name}>
              <Component portalTarget={portalRef} />
            </Suspense>
          ))}

          <UserMenu />
        </div>
      </nav>

      <div className={classes.portal} ref={portalRef} />
    </>
  );
}

export interface CustomSidebarEntryProps {
  portalTarget: React.RefObject<HTMLElement | null>;
}

const customSidebarEntries = Object.entries({
  ...import.meta.glob(['/src/components/sidebar/*.tsx', '../sidebar/*.tsx'], { eager: true }),
})
  .map(([path, module]): [string, object] | undefined => {
    const componentName = path?.match(/.*\/(.*)\.ts/)?.[1];
    if (!componentName || !module || typeof module !== 'object' || !(componentName in module)) {
      return undefined;
    }
    try {
      const component = (module as Record<string, React.FunctionComponent>)[componentName] as React.FunctionComponent;
      console.debug(`Registered sidebar component: '${componentName}'`);
      return [componentName, component];
    } catch (e) {
      console.error(`Error loading sidebar component '${componentName}':`, e);
      return undefined;
    }
  })
  .filter((it): it is [string, React.FunctionComponent<CustomSidebarEntryProps>] => it?.[1] !== undefined);
